// Live cards and agent marks inside a conversation (M2-06; spec 06 §Messages; agents-in-chat
// research recs. 6, 9, 15): a task card that re-renders on task pings and changes status
// inline, an approval card as a message type (Approve / Edit / Reject for whoever may decide,
// "awaiting approval" for everyone else), the cost chip on agent messages, and the ephemeral
// status line while an agent works in the topic. AGT-UI-2: the approval card decides with Agents'
// shared `ApprovalActions` (the same buttons as Agents › Approvals and the inbox), shows a held
// agent post's text, and says when an approved change was not applied.
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  NativeSelect,
  NativeSelectOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  TaskCard,
  type TaskSummary,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  BotIcon,
  ChartLineIcon,
  CircleDollarSignIcon,
  HandIcon,
  LoaderCircleIcon,
  LockIcon,
  PencilIcon,
  SquareCheckIcon,
  SquareIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { Api } from "../../api/client";
import { ApprovalActions, decisionToast, heldPostText } from "../agents/approval-actions";
import { ApprovalExecutionBadge, executionOf } from "../agents/approvals-screen";
import { EscalationCard, hasCard } from "../agents/escalation-card";
import { useWalletOn } from "../billing/use-wallet-on";
import { PlanCheckBadge } from "../de/plan-check-badge";
import { UnverifiedBadge } from "../de/unverified-badge";
import { FlowCard } from "../flows/flow-entry";
import { localDay } from "../work/model";
import { elapsed, formatCost, formatTokens, runStateLabel } from "./chat-model";
import {
  type Approval,
  approvalQuery,
  type Board,
  boardsQuery,
  chatKeys,
  type Run,
  type TaskDetail,
  taskQuery,
} from "./chat-queries";
import type { ChatContext } from "./use-chat";

const STATUS_TONE = { todo: "neutral", active: "info", done: "success" } as const;

/** A seat as a task card shows it: its DE's name (an agent) or the seat's title. */
export type SeatLabel = { name: string; agent: boolean };

function taskSummary(
  t: TaskDetail,
  board: Board | undefined,
  seats: ReadonlyMap<string, SeatLabel>,
): TaskSummary {
  const def = board?.status_set.find((s) => s.key === t.status);
  const kind = def?.done ? "done" : def?.started ? "active" : "todo";
  return {
    id: t.id,
    title: `#${t.number} ${t.title}`,
    status: { label: def?.name ?? t.status, tone: STATUS_TONE[kind], kind },
    done: Boolean(def?.done),
    assignees: t.assignee_seat_ids.map((s) => {
      const seat = seats.get(s);
      return {
        name: seat?.name ?? "Seat",
        kind: seat?.agent ? ("agent" as const) : ("person" as const),
      };
    }),
    ...(t.due_at
      ? { due: localDay(t.due_at), overdue: !def?.done && t.due_at < new Date().toISOString() }
      : {}),
    ...(t.checklist.total ? { counts: { subtasks: t.checklist } } : {}),
    ...(board ? { location: `${board.name}${t.blocked ? " · blocked" : ""}` } : {}),
  };
}

/**
 * A live task card (06: "re-renders on task events, not on reload"; inline status change). A
 * task the viewer may not see renders as "Restricted task"; the message's own make-task result
 * renders as a compact "→ Task #n" chip. DE-REPLY-STYLE: a reply's link to its task
 * (`task_link:` → `chip`) is a small chip that opens the task, so the reply never spells it out.
 */
export function TaskEmbed({
  chat,
  taskId,
  messageId,
  seats,
  chip = false,
}: {
  chat: ChatContext;
  taskId: string;
  messageId: string;
  seats: ReadonlyMap<string, SeatLabel>;
  chip?: boolean;
}) {
  const { api, co } = chat;
  const task = useQuery(taskQuery(api, co, taskId));
  const boards = useQuery(boardsQuery(api, co));
  const queryClient = useQueryClient();
  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api.client.tasks.setStatus({ params: { co, id: taskId }, body: { status } }),
    onSuccess: (t) => queryClient.setQueryData(chatKeys.task(co, taskId), t),
  });
  if (task.isPending)
    return (
      <span
        role="status"
        aria-label="Loading task"
        className="inline-flex items-center gap-2 text-xs text-muted-foreground"
      >
        <Spinner className="size-3" /> Loading task…
      </span>
    );
  if (task.isError || !task.data)
    return (
      <span
        data-slot="task-restricted"
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground"
      >
        <LockIcon aria-hidden="true" className="size-3" />
        Restricted task
      </span>
    );
  const t = task.data;
  const board = boards.data?.data.find((b) => b.id === t.board_id);
  if (chip)
    return (
      <Link
        to="/c/$slug/work"
        params={{ slug: chat.company.slug }}
        search={{ task: t.id } as never}
        data-slot="task-link-chip"
        aria-label={`Open task #${t.number}: ${t.title}`}
        className="inline-flex max-w-full items-center gap-1.5 self-start rounded-md border border-border bg-card px-2 py-0.5 text-xs text-foreground hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
      >
        <SquareCheckIcon aria-hidden="true" className="size-3 shrink-0 text-muted-foreground" />
        <span className="shrink-0 text-muted-foreground">#{t.number}</span>
        <span className="truncate">{t.title}</span>
      </Link>
    );
  if (t.origin_ref?.message_id === messageId)
    return (
      <Badge variant="accent" data-slot="task-chip">
        <ArrowRightIcon aria-hidden="true" />
        Task #{t.number}: {t.title}
      </Badge>
    );
  return (
    <div data-slot="task-embed" className="flex flex-col gap-2">
      <TaskCard variant="inline" task={taskSummary(t, board, seats)} />
      {board && !chat.readOnly ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <label htmlFor={`status-${messageId}-${t.id}`}>Status</label>
          <NativeSelect
            id={`status-${messageId}-${t.id}`}
            size="sm"
            value={t.status}
            disabled={setStatus.isPending}
            onChange={(e) => setStatus.mutate(e.target.value)}
          >
            {board.status_set.map((s) => (
              <NativeSelectOption key={s.key} value={s.key}>
                {s.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
      ) : null}
    </div>
  );
}

/**
 * GUIDED-FLOW: a `flow:<id>` card ref — the flow's title, its progress line and Open (the flow's
 * own page), so a "complete your seat setup" ask stays one line in chat.
 */
export function FlowCardEmbed({ chat, runId }: { chat: ChatContext; runId: string }) {
  return <FlowCard api={chat.api} company={chat.company} runId={runId} />;
}

/**
 * DE-INITIATE: a `metric:<id>` card ref — what a DE's unprompted post is about when it is a KPI:
 * a chip with the KPI's name that opens that KPI on the scorecard (its cell focused, `kpi=`).
 * The name comes from `metrics.get`, which answers only for a KPI the reader may see; anyone
 * else gets "About a KPI" (Codex DE-INITIATE r1 #18).
 */
export function MetricChip({ chat, metricId }: { chat: ChatContext; metricId: string }) {
  const { api, co } = chat;
  const metric = useQuery({
    queryKey: ["metric", co, metricId],
    queryFn: () => api.client.metrics.get({ params: { co, id: metricId } }),
    retry: false,
    staleTime: 60_000,
  });
  const name = metric.data?.name;
  return (
    <Link
      to="/c/$slug/kpis/scorecard"
      params={{ slug: chat.company.slug }}
      // Only the KPI: a seat or team focus carried over could filter its row out (Codex).
      search={{ kpi: metricId }}
      data-slot="metric-chip"
      data-metric-id={metricId}
      className="self-start"
    >
      <Badge variant="outline">
        <ChartLineIcon aria-hidden="true" />
        {name ? `KPI: ${name}` : "About a KPI"}
      </Badge>
    </Link>
  );
}

const APPROVAL_STATE: Record<
  Approval["state"],
  { label: string; tone: "warning" | "success" | "danger" | "info" | "neutral" }
> = {
  pending: { label: "Awaiting approval", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  edited: { label: "Approved with edits", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  expired: { label: "Expired", tone: "neutral" },
};

/**
 * An approval as a message type (research rec. 9): the proposal for whoever may see it, with
 * Approve / Edit / Reject while it is pending; after a decision the card updates in place with
 * who decided. Without `approval#viewer` (or the agents add-on) it says "awaiting approval"
 * and nothing more.
 */
export function ApprovalEmbed({ chat, approvalId }: { chat: ChatContext; approvalId: string }) {
  const { api, co } = chat;
  const approval = useQuery({ ...approvalQuery(api, co, approvalId), enabled: chat.agents });
  if (!chat.agents || approval.isError || (!approval.isPending && !approval.data)) {
    const hidden = approval.error instanceof ApiError && approval.error.status === 404;
    return (
      <div
        data-slot="approval-card"
        data-state="hidden"
        className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground"
      >
        <HandIcon aria-hidden="true" className="size-4" />
        {hidden || !chat.agents ? "Awaiting approval" : "This approval could not be loaded"}
      </div>
    );
  }
  if (approval.isPending)
    return (
      <span
        role="status"
        aria-label="Loading approval"
        className="inline-flex items-center gap-2 text-xs text-muted-foreground"
      >
        <Spinner className="size-3" /> Loading approval…
      </span>
    );
  const a = approval.data;
  const state = APPROVAL_STATE[a.state];
  // AGT-UI-2: a held agent post shows its text; the decision is Agents' own ApprovalActions.
  // Approved with edits: the text the agent posts is the approved one, not the proposal's.
  const post =
    a.state === "edited" && a.approved_payload
      ? heldPostText({ kind: a.kind, proposal: a.approved_payload })
      : heldPostText(a);
  const asker = a.requested_by_membership_id
    ? chat.members.get(a.requested_by_membership_id)?.name
    : undefined;
  const heading = post !== null ? `${asker ?? "An agent"} wants to post` : a.proposal.title;
  const decider = a.decided_by_membership_id
    ? (chat.members.get(a.decided_by_membership_id)?.name ?? "Someone")
    : null;
  // Phase 1 inc 2: what became of an approved change ("Approved · not applied: …").
  const execution = executionOf(a);
  return (
    <article
      data-slot="approval-card"
      data-state={a.state}
      data-approval={a.id}
      aria-label={`Approval: ${heading}`}
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3 text-card-foreground"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <HandIcon aria-hidden="true" className="size-4 shrink-0 text-warning-text" />
        <span className="min-w-0 flex-1 text-base font-medium text-foreground">{heading}</span>
        {/* DC-05: raised by an unverified DE (a pilot or an empty self-serve agent). */}
        {a.proposal.unverified ? <UnverifiedBadge /> : null}
        {/* PLAN-CHECK: the DE's memory check of its plan */}
        <PlanCheckBadge check={a.proposal.plan_check} />
        <Badge variant={state.tone} dot>
          {state.label}
        </Badge>
        <ApprovalExecutionBadge approval={a} />
      </div>
      {hasCard(a) ? (
        // CORE-CARDS: the escalation card (situation, options, recommended, approver, deadline)
        <EscalationCard
          api={api}
          co={co}
          approval={a}
          decide={!chat.readOnly}
          onDecided={(d) => toast.add(decisionToast(d))}
        />
      ) : post !== null ? (
        <blockquote
          data-slot="held-post"
          className="m-0 border-l-2 border-border pl-3 text-sm whitespace-pre-wrap text-secondary"
        >
          {post}
        </blockquote>
      ) : a.proposal.summary ? (
        <p className="text-sm text-secondary">{a.proposal.summary}</p>
      ) : null}
      {a.state === "pending" && hasCard(a) ? null : a.state === "pending" ? (
        chat.readOnly ? null : (
          <ApprovalActions
            api={api}
            co={co}
            approval={a}
            size="sm"
            onDecided={(d) =>
              toast.add(decisionToast(d, post !== null ? "Approved: posted" : "Approved"))
            }
          />
        )
      ) : (
        <p className="text-xs text-muted-foreground">
          {state.label}
          {decider ? ` by ${decider}` : ""}
          {a.reason ? `: “${a.reason}”` : ""}
          {execution && execution.tone !== "success" ? (
            <span
              className={execution.tone === "danger" ? "block text-destructive" : "block"}
              data-slot="approval-execution"
            >
              {execution.text}
            </span>
          ) : null}
        </p>
      )}
    </article>
  );
}

// SCREENS-NEEDS-YOU / SCREEN-HANDOFF: the needs-you card lives in its own file.
export { ScreenAskEmbed, screenAskWhat } from "./screen-ask-card";

/**
 * The cost chip on an agent message (research rec. 15): the run's cost, expandable to model,
 * tokens and state. A run on a subscription path shows its estimate, never $0.
 */
export function CostChip({ run, api, co }: { run: Run; api: Api; co: string }) {
  // A wallet company sees dollars only: the token counts stay in the wallet entry detail.
  const walletOn = useWalletOn(api, co);
  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            data-slot="cost-chip"
            title="Cost of this run"
            aria-label={`Run cost ${formatCost(run.cost_micros)}`}
            className="inline-flex h-5 items-center gap-1 rounded-full border border-border bg-card px-1.5 text-2xs font-medium text-secondary tabular-nums hover:border-strong focus-visible:outline-2 focus-visible:outline-ring"
          />
        }
      >
        <CircleDollarSignIcon aria-hidden="true" className="size-3" />
        {formatCost(run.cost_micros).replace("$", "")}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 text-sm">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <dt className="text-muted-foreground">Cost</dt>
          <dd className="tabular-nums">{formatCost(run.cost_micros)}</dd>
          {walletOn ? null : (
            <>
              <dt className="text-muted-foreground">Tokens</dt>
              <dd className="tabular-nums">
                {formatTokens(run.tokens_in)} in · {formatTokens(run.tokens_out)} out
              </dd>
            </>
          )}
          <dt className="text-muted-foreground">Model</dt>
          <dd>{run.model ?? "—"}</dd>
          <dt className="text-muted-foreground">Run</dt>
          <dd>{runStateLabel(run)}</dd>
        </dl>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The ephemeral status line while agents work in a topic (research rec. 6): not a message, it
 * ticks the elapsed time, offers Stop, and disappears when the run ends (the runs query
 * refetches every few seconds while one is in flight, so a stuck line cannot outlive its run).
 */
export function AgentStatusLine({
  chat,
  runs,
  typing,
}: {
  chat: ChatContext;
  runs: readonly Run[];
  typing: readonly string[];
}) {
  const { api, co } = chat;
  const [now, setNow] = useState(() => new Date());
  const queryClient = useQueryClient();
  const stop = useMutation({
    mutationFn: (id: string) => api.client.runs.cancel({ params: { co, id } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.runs(co) });
      toast.add({ title: "Stopped", type: "success" });
    },
  });
  const ticking = runs.some((r) => r.state === "running" || r.state === "queued");
  useEffect(() => {
    if (!ticking) return;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [ticking]);
  if (!runs.length && !typing.length) return null;
  return (
    <div
      data-slot="agent-status"
      role="status"
      aria-live="polite"
      className="flex flex-col gap-1 px-1 text-xs text-muted-foreground"
    >
      {runs.map((r) => {
        const name = chat.members.get(r.membership_id)?.name ?? "An agent";
        const working = r.state === "running" || r.state === "queued";
        return (
          <div key={r.id} data-run-state={r.state} className="flex min-w-0 items-center gap-2">
            {working ? (
              // Not a Spinner: that announces "Loading", and an agent at work is not a page load.
              <LoaderCircleIcon aria-hidden="true" className="size-3 shrink-0 animate-spin" />
            ) : (
              <BotIcon aria-hidden="true" className="size-3.5 shrink-0" />
            )}
            <span className="min-w-0 truncate">
              <span className="font-medium text-secondary">{name}</span> is {runStateLabel(r)}
              {working && r.started_at ? (
                <span className="tabular-nums"> · {elapsed(r.started_at, now)}</span>
              ) : null}
              {working ? " · messages you send now join its next turn" : ""}
            </span>
            {working && !chat.readOnly ? (
              <Button
                variant="ghost"
                size="xs"
                className="ml-auto shrink-0"
                disabled={stop.isPending}
                onClick={() => stop.mutate(r.id)}
              >
                <SquareIcon data-icon="inline-start" />
                Stop
              </Button>
            ) : null}
          </div>
        );
      })}
      {typing.length ? (
        <div className="flex items-center gap-2">
          <PencilIcon aria-hidden="true" className="size-3" />
          {typing.join(", ")} {typing.length === 1 ? "is" : "are"} typing…
        </div>
      ) : null}
    </div>
  );
}
